import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Lock, Ticket, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/* RoyAI Lock Gate — automated daily-drop version.
 * - Fetches /royai/vip/drop for the live counter (public, no auth)
 * - Fetches /royai/access-status to see if the user already claimed today
 * - "Claim Invitation" button hits /royai/vip/claim and auto-fills the code
 * - Once claimed, the 60-minute countdown ticks live
 * - When the drop is sold out, shows the "next drop in HH:MM:SS" countdown */
const LOCK_VIDEO =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_producer-health/artifacts/dbdqnx8b_ROYAI-%20LOCK%20ANIMATION.mp4";

const hms = (s) => {
  s = Math.max(0, Math.floor(s));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return h === "00" ? `${m}:${sec}` : `${h}:${m}:${sec}`;
};

export default function RoyaiLockGate({ onUnlock, activeClaim }) {
  const [drop, setDrop] = useState(null);
  const [claim, setClaim] = useState(activeClaim || null);
  const [code, setCode] = useState(activeClaim?.code || "");
  const [claiming, setClaiming] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [tick, setTick] = useState(0);

  const loadDrop = async () => {
    try {
      const { data } = await api.get("/royai/vip/drop");
      setDrop(data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    loadDrop();
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    const s = setInterval(loadDrop, 20000);
    return () => {
      clearInterval(t);
      clearInterval(s);
    };
  }, []);

  const claimInvitation = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post("/royai/vip/claim");
      setClaim(data);
      setCode(data.code);
      toast.success(data.already_claimed ? `Your code today is ${data.code}` : `VIP code ${data.code} claimed`);
      await loadDrop();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "sold_out") {
        toast.error("Sold out for today. Watch the next-drop countdown.");
        await loadDrop();
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setClaiming(false);
    }
  };

  const redeem = async (e) => {
    e?.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      toast.error("Enter your VIP code first.");
      return;
    }
    setRedeeming(true);
    try {
      await api.post("/royai/redeem-code", { code: trimmed });
      toast.success("VIP access granted. Welcome to RoyAI.");
      onUnlock?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not verify that code.");
    } finally {
      setRedeeming(false);
    }
  };

  void tick;
  const claimExpiresIn = claim?.expires_at ? Math.floor((new Date(claim.expires_at).getTime() - Date.now()) / 1000) : 0;
  const nextDropIn = drop?.seconds_to_next_drop ? Math.floor(new Date(drop.next_drop_at).getTime() / 1000 - Date.now() / 1000) : 0;
  const soldOut = drop?.remaining === 0;

  return (
    <div
      className="min-h-[70vh] flex items-center justify-center px-6 py-16 bg-black"
      data-testid="royai-lock-gate"
    >
      <div className="w-full max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 border border-amber-500/30 px-3 py-1.5 text-[10px] uppercase tracking-widest text-amber-400 font-mono-tech mb-4">
            <Lock size={11} /> RoyAI · Restricted
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium text-white tracking-tight mb-3">
            VIP Access <span className="text-amber-500">Only.</span>
          </h1>
          <p className="text-sm text-neutral-400 max-w-xl mx-auto">
            Request a 60-Minute Invite Code to open the Royalty Hunter, Deep
            Dive Report, Analyzer, and Verified Portfolio.
          </p>
        </div>

        {/* Cinematic lock video */}
        <div className="relative mb-6" data-testid="royai-lock-video-wrap">
          <div
            className="absolute -inset-3 sm:-inset-4 pointer-events-none blur-2xl opacity-70"
            style={{
              background:
                "radial-gradient(60% 60% at 50% 40%, rgba(212,175,55,0.55) 0%, rgba(212,175,55,0.18) 45%, transparent 75%)",
            }}
            aria-hidden="true"
          />
          <div className="relative border border-amber-500/40 shadow-[0_0_60px_-10px_rgba(212,175,55,0.55)] bg-black overflow-hidden">
            <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
              <video
                data-testid="royai-lock-video"
                src={LOCK_VIDEO}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-amber-500 pointer-events-none" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-amber-500 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-amber-500 pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-amber-500 pointer-events-none" />
              <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-amber-500/40 px-2.5 py-1">
                <span className="w-1.5 h-1.5 bg-amber-500 animate-pulse rounded-full" />
                <span className="text-[10px] font-mono-tech uppercase tracking-widest text-amber-400">
                  Vault · Secured
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Drop status — stealth scarcity, no numeric counter */}
        {drop && (
          <div
            className="bg-[#0c0c0c] border border-amber-500/25 p-4 sm:p-5 mb-4 flex flex-wrap items-center justify-between gap-4"
            data-testid="vip-drop-counter"
          >
            <div className="flex items-center gap-3">
              <Ticket size={16} className="text-amber-500" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  VIP Invite Codes
                </div>
                <div className="font-display text-lg text-white" data-testid="drop-status-label">
                  {drop.sold_out ? (
                    <span className="text-red-300">Enrollment Closed for Today</span>
                  ) : (
                    <span>Invite Windows <span className="text-amber-500">Open</span></span>
                  )}
                </div>
              </div>
            </div>
            {drop.sold_out && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  Next window in
                </div>
                <div className="font-mono-tech text-lg text-amber-400 flex items-center justify-end gap-1.5">
                  <Clock size={12} /> {hms(nextDropIn)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Claim / active claim / sold out banner */}
        {claim ? (
          <div
            className="bg-gradient-to-br from-[#141414] to-[#0c0c0c] border border-amber-500/40 p-5 mb-4 flex flex-wrap items-center gap-4"
            data-testid="active-claim-banner"
          >
            <KeyRound size={20} className="text-amber-500 shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Your VIP Code</div>
              <div className="font-mono-tech text-2xl tracking-[0.35em] text-amber-500" data-testid="active-claim-code">
                {claim.code}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Expires in</div>
              <div className="font-mono-tech text-lg text-amber-400" data-testid="active-claim-countdown">
                {hms(claimExpiresIn)}
              </div>
            </div>
          </div>
        ) : soldOut ? (
          <div
            className="bg-red-950/30 border border-red-500/40 p-5 mb-4 text-center"
            data-testid="sold-out-banner"
          >
            <div className="text-red-200 font-display text-lg mb-1">
              VIP invitations for today are closed.
            </div>
            <div className="text-xs text-neutral-400">
              Next window opens in <span className="text-amber-400 font-mono-tech">{hms(nextDropIn)}</span>
            </div>
          </div>
        ) : (
          <button
            onClick={claimInvitation}
            disabled={claiming || !drop}
            data-testid="claim-invitation-btn"
            className="w-full mb-4 inline-flex items-center justify-center gap-2 bg-amber-500 text-black font-semibold px-6 py-3.5 hover:brightness-110 transition-all disabled:opacity-40"
          >
            {claiming ? (
              <><Loader2 size={14} className="animate-spin" /> Requesting…</>
            ) : (
              <><Ticket size={14} /> Request 60-Minute Invite Code</>
            )}
          </button>
        )}

        {/* Code redemption (auto-filled if the user has an active claim) */}
        <form
          onSubmit={redeem}
          className="bg-[#0c0c0c] border border-white/10 p-5 sm:p-6 flex flex-col sm:flex-row gap-3 items-stretch"
        >
          <div className="flex items-center gap-2 flex-1 bg-[#050505] border border-white/10 px-3">
            <KeyRound size={14} className="text-amber-500 shrink-0" />
            <input
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\s+/g, "").toUpperCase().slice(0, 16))
              }
              placeholder="VIP CODE"
              spellCheck={false}
              autoComplete="off"
              data-testid="royai-code-input"
              className="w-full bg-transparent px-2 py-3 text-white font-mono-tech tracking-[0.3em] outline-none placeholder:text-neutral-700"
            />
          </div>
          <button
            type="submit"
            disabled={redeeming || !code.trim()}
            data-testid="royai-code-submit"
            className="inline-flex items-center justify-center gap-2 bg-amber-500 text-black font-semibold px-6 py-3 hover:brightness-110 transition-all disabled:opacity-40"
          >
            {redeeming ? (
              <><Loader2 size={14} className="animate-spin" /> Verifying…</>
            ) : (
              <>Unlock RoyAI</>
            )}
          </button>
        </form>

        <p className="text-[11px] text-neutral-600 mt-3 text-center">
          Each invite code opens a 60-minute access window from the moment it&apos;s requested.
        </p>
      </div>
    </div>
  );
}
